import { renderPageShell } from '../lib/page-shell';
import { equipCosmetic, fetchCosmetics, rememberEquippedTheme, type CosmeticRow } from '../lib/cosmetics';
import { fetchMe } from '../lib/session';
import type { RouteHandler } from '../lib/router';

export const renderCustomise: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'customise', {
    title: 'CUSTOMISE',
    subtitle: 'Manage and equip the timer faces you own. Free faces unlock instantly; others unlock by level.',
    body: `<div id="cosmetics-mount"><p class="page-body__stub">Loading…</p></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#cosmetics-mount')!;

  // Kept in closure state so an equip re-renders from memory rather than
  // refetching the whole catalogue over the network (owner report 2026-08-18:
  // equipping felt like nothing happened for several seconds). The only round
  // trip on an equip is now the single POST that performs it.
  let catalog: CosmeticRow[] = [];
  const owned = new Set<string>();
  let equippedId: string | undefined;
  let level = 1;

  /**
   * Customise is for what you already own or can earn, not a second shopfront
   * (owner decision 2026-08-18: it used to also list every purchasable Store
   * item with "Buy in Store" buttons, which duplicated and under-represented
   * the Store). So: free faces, anything owned, and level-locked faces you can
   * work toward. Purchasable faces you do not own are intentionally absent;
   * the footer sends you to the Store for those.
   */
  function visibleCatalog(): CosmeticRow[] {
    return catalog.filter(
      (cosmetic) => cosmetic.unlock_type === 'free' || cosmetic.unlock_type === 'level' || owned.has(cosmetic.id),
    );
  }

  function renderGrid() {
    const items = visibleCatalog();
    mount.innerHTML = `
      <div class="cosmetics-grid">
        ${items
          .map((cosmetic) =>
            renderCosmeticCard(cosmetic, { owned: owned.has(cosmetic.id), equipped: cosmetic.id === equippedId, level }),
          )
          .join('')}
      </div>
      <div class="customise-more">
        <p class="customise-more__lead">Looking for more?</p>
        <div class="customise-more__actions">
          <button class="button button--primary" type="button" data-action="store">Visit the Store</button>
          <button class="button button--secondary" type="button" data-action="achievements">View your Achievements to unlock more</button>
        </div>
      </div>
    `;

    mount.querySelector<HTMLButtonElement>('[data-action="store"]')?.addEventListener('click', () => navigate('store'));
    mount.querySelector<HTMLButtonElement>('[data-action="achievements"]')?.addEventListener('click', () => navigate('achievements'));

    mount.querySelectorAll<HTMLButtonElement>('[data-action="equip"]').forEach((button) => {
      button.addEventListener('click', () => void handleEquip(button));
    });
  }

  async function handleEquip(button: HTMLButtonElement) {
    const cosmeticId = button.dataset.cosmeticId!;
    const previousEquipped = equippedId;
    const previouslyOwned = owned.has(cosmeticId);

    // Optimistic: reflect the equip immediately, then reconcile if the POST fails.
    button.disabled = true;
    button.textContent = 'Equipping…';
    owned.add(cosmeticId);
    equippedId = cosmeticId;
    const cosmetic = catalog.find((row) => row.id === cosmeticId);
    if (cosmetic) rememberEquippedTheme(cosmetic);

    try {
      await equipCosmetic(cosmeticId);
      renderGrid();
    } catch (error) {
      // Revert optimistic state.
      equippedId = previousEquipped;
      if (!previouslyOwned) owned.delete(cosmeticId);
      renderGrid();
      const reverted = mount.querySelector<HTMLButtonElement>(`[data-cosmetic-id="${cosmeticId}"]`);
      if (reverted) reverted.textContent = error instanceof Error ? error.message : 'Could not equip.';
    }
  }

  function renderCosmeticCard(cosmetic: CosmeticRow, state: { owned: boolean; equipped: boolean; level: number }): string {
    const lockLabel =
      cosmetic.unlock_type === 'level'
        ? state.owned
          ? 'Owned'
          : `Unlocks at level ${cosmetic.unlock_level}${state.level >= (cosmetic.unlock_level ?? 0) ? ' (ready)' : ''}`
        : cosmetic.unlock_type === 'purchase'
          ? 'Owned'
          : 'Free';

    const canUnlockNow = cosmetic.unlock_type === 'level' && state.level >= (cosmetic.unlock_level ?? 0);
    const canEquip = state.owned || cosmetic.unlock_type === 'free' || canUnlockNow;
    const buttonDisabled = state.equipped || !canEquip;

    return `
      <div class="choice-card cosmetic-card">
        <div class="cosmetic-card__swatch" style="background:${cosmetic.background_color}; border-color:${cosmetic.accent_color};">
          <span style="background:${cosmetic.accent_color};"></span>
        </div>
        <strong>${cosmetic.name}</strong>
        <small>${lockLabel}</small>
        <button
          class="button ${state.equipped ? 'button--primary' : 'button--secondary'}"
          type="button"
          data-action="${buttonDisabled ? '' : 'equip'}"
          data-cosmetic-id="${cosmetic.id}"
          ${buttonDisabled ? 'disabled' : ''}
        >
          ${state.equipped ? 'Equipped' : !state.owned && canUnlockNow ? 'Unlock & equip' : state.owned ? 'Equip' : `Reach level ${cosmetic.unlock_level}`}
        </button>
      </div>
    `;
  }

  async function load() {
    const me = await fetchMe();
    if (!me) {
      mount.innerHTML = `
        <div class="handoff">
          <p class="handoff__label">Sign in required</p>
          <h2>Cosmetics are tied to your account</h2>
          <button class="button button--primary" type="button" data-action="account">Go to account</button>
        </div>
      `;
      mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
      return;
    }

    const cosmetics = await fetchCosmetics();
    catalog = cosmetics.catalog;
    level = cosmetics.level;
    owned.clear();
    cosmetics.inventory.forEach((row) => owned.add(row.cosmetic_id));
    equippedId = cosmetics.inventory.find((row) => row.equipped)?.cosmetic_id;
    renderGrid();
  }

  void load();
};
